import { graphqlClient } from '@/lib/graphql-client';
import { postHrmApi, runHrmApi } from '@/lib/hrm-api';
import { findWorkflowOutput } from '@/lib/workflow-webhook';
import { weekdayNameOf } from '@/lib/utils/custom-date/custom-date';
import { buildGetLunchStatusQuery, buildGetMyLunchRequestsQuery } from '../graphql/queries';
import {
  GetLunchRequestsParams,
  LunchCreateResult,
  LunchMenu,
  LunchOrder,
  LunchRequestsPagingResult,
  LunchStatus,
  lunchStatusOf,
} from '../types/lunch.types';

/** The lunch page's state for `userId` on lunch day `day` ("YYYY-MM-DD", from getTargetLunchDay). */
export const getLunchStatus = async (userId: string, day: string): Promise<LunchStatus> => {
  const weekDay = weekdayNameOf(day) ?? '';
  const result = await graphqlClient.query<{
    Orders: { items: LunchOrder[] };
    Menus: { items: LunchMenu[] };
  }>({ query: buildGetLunchStatusQuery(userId, day, weekDay) });
  return lunchStatusOf(day, weekDay, result.Orders?.items?.[0], result.Menus?.items ?? []);
};

export const getMyLunchRequests = async (
  params: GetLunchRequestsParams
): Promise<{ LunchRequests: LunchRequestsPagingResult }> => {
  const { pageNo, pageSize, filter = {}, order } = params;
  return graphqlClient.query({
    query: buildGetMyLunchRequestsQuery(filter, pageNo, pageSize, order),
  }) as Promise<{ LunchRequests: LunchRequestsPagingResult }>;
};

export const requestLunch = async (lunchMenuId: string): Promise<LunchCreateResult> => {
  const result = findWorkflowOutput<LunchCreateResult>(
    await postHrmApi('LUNCH_CREATE', {
      lunchMenuId,
    }),
    'IsSuccessful'
  );

  if (!result) throw new Error('LUNCH_REQUEST_FAILED');
  return result;
};

/** Cancels the caller's own order while its day is still open (LUNCH_CANCEL); a refusal throws its
 * translation key. */
export const cancelLunchRequest = (itemId: string) =>
  runHrmApi('LUNCH_CANCEL', { itemId }, 'LUNCH_SAVE_FAILED');
